import { CUR8_SERVER } from "gatsby-env-variables";
import { refreshOnce } from "@components/Login/helpers/refreshOnce";
import { VIEW_AS_HEADER } from "./viewAsRequest";

const END_TIMEOUT_MS = 5000;

const endOnce = async (sessionId: string): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), END_TIMEOUT_MS);
  try {
    // keepalive lets the request outlive a tab closed straight after Exit.
    return await fetch(`${CUR8_SERVER}/impersonation-sessions/current/end`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { [VIEW_AS_HEADER]: sessionId },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

// A refresh that never answers must not hold the investor's screen: Exit waits the same bound as the end call.
const refreshedInTime = async (): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), END_TIMEOUT_MS);
  });

  try {
    const refreshed = await Promise.race([refreshOnce(), bound]);
    return refreshed?.status === 201;
  } finally {
    clearTimeout(timer);
  }
};

/** Ends the session on invest-api and answers whether the server confirmed it. */
export const endViewAsSessionOnServer = async (
  sessionId: string
): Promise<boolean> => {
  let response = await endOnce(sessionId);

  // An expired staff login answers 401: share the refresh the interceptors may already be running, then try once more.
  if (response.status === 401 && (await refreshedInTime())) {
    response = await endOnce(sessionId);
  }

  return response.ok;
};
